/**
 * Notification System
 * 
 * Manages toast notifications for game events (building completed, resources full, etc.)
 */

export class NotificationSystem {
    constructor(containerElement = null) {
        // Create container if not provided
        this.container = containerElement || this.createContainer();
        this.notifications = [];
        this.nextId = 1;
        this.defaultDuration = 3000; // 3 seconds
    }
    
    /**
     * Create notification container if not provided
     * @returns {HTMLElement} Container element
     */
    createContainer() {
        let container = document.getElementById('notification-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'notification-container';
            container.className = 'notification-container';
            document.body.appendChild(container);
        }
        return container;
    }
    
    /**
     * Show a notification
     * @param {string} message - Notification message
     * @param {string} type - Notification type ('info', 'success', 'warning', 'error')
     * @param {number} duration - Duration in milliseconds (0 = permanent until dismissed)
     * @returns {number} Notification ID
     */
    show(message, type = 'info', duration = null) {
        const id = this.nextId++;
        const notification = {
            id,
            message,
            type,
            duration: duration !== null ? duration : this.defaultDuration,
            element: null,
            timer: null
        };
        
        this.notifications.push(notification);
        this.renderNotification(notification);
        
        // Auto-dismiss if duration is set
        if (notification.duration > 0) {
            notification.timer = setTimeout(() => {
                this.dismiss(id);
            }, notification.duration);
        }
        
        return id;
    }
    
    /**
     * Render a notification element
     * @param {Object} notification - Notification object
     */
    renderNotification(notification) {
        const element = document.createElement('div');
        element.className = `notification notification-${notification.type}`;
        element.dataset.notificationId = notification.id;
        
        element.innerHTML = `
            <div class="notification-content">
                <span class="notification-message">${this.escapeHtml(notification.message)}</span>
                <button class="notification-close" aria-label="Close notification">×</button>
            </div>
        `;
        
        // Add close button handler
        const closeBtn = element.querySelector('.notification-close');
        closeBtn.addEventListener('click', () => {
            this.dismiss(notification.id);
        });
        
        // Add to container
        this.container.appendChild(element);
        notification.element = element;
        
        // Trigger animation
        requestAnimationFrame(() => {
            element.classList.add('notification-show');
        });
    }
    
    /**
     * Dismiss a notification
     * @param {number} id - Notification ID
     */
    dismiss(id) {
        const index = this.notifications.findIndex(n => n.id === id);
        if (index === -1) return;
        
        const notification = this.notifications[index];
        
        // Clear timer
        if (notification.timer) {
            clearTimeout(notification.timer);
        }
        
        // Animate out
        if (notification.element) {
            notification.element.classList.remove('notification-show');
            notification.element.classList.add('notification-hide');
            
            setTimeout(() => {
                if (notification.element && notification.element.parentNode) {
                    notification.element.parentNode.removeChild(notification.element);
                }
            }, 300); // Match CSS transition duration
        }
        
        // Remove from array
        this.notifications.splice(index, 1);
    }
    
    /**
     * Dismiss all notifications
     */
    dismissAll() {
        const ids = this.notifications.map(n => n.id);
        ids.forEach(id => this.dismiss(id));
    }
    
    /**
     * Show success notification
     * @param {string} message - Message
     * @param {number} duration - Duration in milliseconds
     */
    success(message, duration = null) {
        return this.show(message, 'success', duration);
    }
    
    /**
     * Show info notification
     * @param {string} message - Message
     * @param {number} duration - Duration in milliseconds
     */
    info(message, duration = null) {
        return this.show(message, 'info', duration);
    }
    
    /**
     * Show warning notification
     * @param {string} message - Message
     * @param {number} duration - Duration in milliseconds
     */
    warning(message, duration = null) {
        return this.show(message, 'warning', duration);
    }
    
    /**
     * Show error notification
     * @param {string} message - Message
     * @param {number} duration - Duration in milliseconds (default: 5000 for errors)
     */
    error(message, duration = 5000) {
        return this.show(message, 'error', duration);
    }
    
    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

